<template>
    <div class="readings-container">
        <div class="readings-section">
            <div class="readings-section-header">
                <span class="readings-section-label">
                    <i class="pi pi-wave-pulse" style="font-size: 0.85rem"></i>
                    Monitor Readings
                </span>
            </div>
            <div v-if="monitorReadings.length" class="readings-grid">
                <reading-cell
                    v-for="monitorReading of monitorReadings"
                    :key="monitorReading.readingType"
                    :reading-type="monitorReading.readingType"
                    :accountId="accountId"
                    :vesselId="vesselId"
                    :title="monitorReading.abbreviation"
                    :reading="monitorReading"
                    :show-raw="true"
                    :vessel-timezone-id="vesselTimezoneId"
                    @updated-reading="updatedReading"
                />
            </div>
            <div v-else class="no-readings">
                <i class="pi pi-info-circle"></i>
                No Current Readings
            </div>
        </div>

        <Divider layout="vertical" class="hidden-mobile" />
        <Divider class="visible-mobile" />

        <div class="readings-section">
            <div class="readings-section-header">
                <span class="readings-section-label">
                    <i class="pi pi-file-edit" style="font-size: 0.85rem"></i>
                    Water Report Readings
                </span>
                <Button @click="showWaterReportHistory" label="History" icon="pi pi-history" size="small" severity="secondary" text />
            </div>
            <div v-if="reportReadings.length" class="readings-grid">
                <reading-cell
                    v-for="reportReading of reportReadings"
                    :key="reportReading.readingType"
                    :reading-type="reportReading.readingType"
                    :accountId="accountId"
                    :vesselId="vesselId"
                    :title="reportReading.abbreviation"
                    :reading="reportReading"
                    :show-raw="false"
                    :vessel-timezone-id="vesselTimezoneId"
                    @updated-reading="updatedReading"
                />
            </div>
            <div v-else class="no-readings">
                <i class="pi pi-info-circle"></i>
                No Current Readings
            </div>
        </div>

        <template v-if="computedReadingsArray.length">
            <Divider layout="vertical" class="hidden-mobile" />
            <Divider class="visible-mobile" />

            <div class="readings-section">
                <div class="readings-section-header">
                    <span class="readings-section-label">
                        <i class="pi pi-calculator" style="font-size: 0.85rem"></i>
                        Computed Readings
                    </span>
                </div>
                <div class="readings-grid">
                    <reading-cell
                        v-for="reading of computedReadingsArray"
                        :key="reading.readingType"
                        :reading-type="reading.readingType"
                        :accountId="accountId"
                        :vesselId="vesselId"
                        :title="reading.abbreviation"
                        :reading="reading"
                        :show-raw="false"
                        raw-title="COMPUTED"
                        :vessel-timezone-id="vesselTimezoneId"
                        @updated-reading="updatedReading"
                    />
                </div>
            </div>
        </template>
    </div>

    <div v-if="lowTempMode !== undefined" class="mt-3">
        <low-temp-mode :low-temp-mode="lowTempMode" :vessel-timezone-id="vesselTimezoneId" />
    </div>
</template>

<script lang="ts" setup>
import { computed, type PropType } from 'vue'
import ReadingCell from '@/components/ReadingCell.vue'
import type { ReadingsV1, VesselStatusV1LowTempMode } from '@geckoal/gecko-api-client'
import router from '@/router'
import LowTempMode from '@/components/vessels/LowTempMode.vue'
import { toReadingsArray } from '@/lib/readings'
import Divider from 'primevue/divider'
import Button from 'primevue/button'

const props = defineProps({
    accountId: {
        type: Number
    },
    vesselId: {
        type: Number
    },
    monitorReadings: {
        type: Object as PropType<ReadingsV1>
    },
    reportReadings: {
        type: Object as PropType<ReadingsV1>
    },
    computedReadings: {
        type: Object as PropType<ReadingsV1>
    },
    lowTempMode: {
        type: Object as PropType<VesselStatusV1LowTempMode>
    },
    vesselTimezoneId: {
        type: String
    }
})

const monitorReadings = computed(() => {
    return toReadingsArray(props.monitorReadings)
})

const reportReadings = computed(() => {
    return toReadingsArray(props.reportReadings)
})

const computedReadingsArray = computed(() => {
    return toReadingsArray(props.computedReadings)
})

const emit = defineEmits(['updatedReading'])

const updatedReading = () => {
    emit('updatedReading')
}

const showWaterReportHistory = () => {
    router.push({ name: 'WaterReportHistory', params: { accountId: props.accountId, vesselId: props.vesselId } })
}
</script>

<style lang="scss" scoped>
.readings-container {
    display: flex;
    gap: 8px;
    align-items: stretch;

    @media (max-width: 960px) {
        flex-direction: column;
    }
}

.readings-section {
    flex: 1;
    min-width: 0;
}

.readings-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    padding-bottom: 6px;
}

.readings-section-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #555;
}

.readings-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.no-readings {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #999;
    font-size: 0.85rem;
    padding: 12px 0;
}

.hidden-mobile {
    @media (max-width: 960px) {
        display: none;
    }
}

.visible-mobile {
    display: none;
    @media (max-width: 960px) {
        display: block;
    }
}
</style>
